import {useCallback, useEffect, useState} from 'react';
import createAxiosInstance from '../support/AxiosSupport';

/**
 * Согласие на AI-профилирование при анализе фото в чате (CTHS-187, GDPR ст.22 / Recital 71).
 *
 * Photo-in-chat — opt-in: до явного согласия функция заблокирована (барьер показывается при первом
 * нажатии «прикрепить фото»). Хук читает текущее согласие и умеет его выдать/отозвать через
 * существующую consent-инфраструктуру (`PUT /privacy/consent`). Чтобы не затронуть остальные
 * согласия, при сохранении переотправляем их текущие значения вместе с `photoProfilingConsent`.
 *
 * Точку «показать барьер, если не выдано» встраивает в кнопку прикрепления фото CTHS-184/186.
 */
interface ConsentState {
    aiProfilingConsent: boolean;
    recommendationsConsent: boolean;
    photoProfilingConsent: boolean;
}

export interface PhotoProfilingConsent {
    granted: boolean;
    loading: boolean;
    saving: boolean;
    refresh: () => Promise<void>;
    setGranted: (granted: boolean) => Promise<boolean>;
}

const DEFAULTS: ConsentState = {
    aiProfilingConsent: true,
    recommendationsConsent: true,
    photoProfilingConsent: false,
};

export default function usePhotoProfilingConsent(): PhotoProfilingConsent {
    const [state, setState] = useState<ConsentState>(DEFAULTS);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const api = await createAxiosInstance();
            const res = await api.get<ConsentState>('/privacy/consent');
            setState({
                aiProfilingConsent: res.data.aiProfilingConsent,
                recommendationsConsent: res.data.recommendationsConsent,
                photoProfilingConsent: res.data.photoProfilingConsent,
            });
        } catch (e) {
            // opt-in: при ошибке считаем, что согласие не выдано (функция заблокирована)
            setState(DEFAULTS);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            await refresh();
            if (cancelled) {
                // компонент размонтирован — состояние уже не используется
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [refresh]);

    const setGranted = useCallback(async (granted: boolean): Promise<boolean> => {
        setSaving(true);
        try {
            const api = await createAxiosInstance();
            await api.patch('/privacy/consent', {
                aiProfilingConsent: state.aiProfilingConsent,
                recommendationsConsent: state.recommendationsConsent,
                photoProfilingConsent: granted,
            });
            setState(prev => ({...prev, photoProfilingConsent: granted}));
            return true;
        } catch (e) {
            return false;
        } finally {
            setSaving(false);
        }
    }, [state.aiProfilingConsent, state.recommendationsConsent]);

    return {granted: state.photoProfilingConsent, loading, saving, refresh, setGranted};
}
